<template>
  <el-dialog
    :title="title"
    custom-class="download-progress-dialog"
    :visible.sync="visible"
    width="720px"
    :show-close="false"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :modal="false"
    :lock-scroll="false"
    append-to-body
  >
    <div class="zd-progress-row">
      <el-progress :percentage="percent" :show-text="false" />
      <span class="zd-percent">{{ percent }}%</span>
    </div>
    <div class="zd-text" aria-live="polite">{{ text }}</div>

    <div class="zd-summary">
      <span>共 {{ files.length }} 个</span>
      <span class="zd-summary-done">已完成 {{ doneCount }}</span>
      <span class="zd-summary-active">下载中 {{ downloadingCount }}</span>
      <span>待下载 {{ pendingCount }}</span>
      <span v-if="failedCount" class="zd-summary-failed">失败 {{ failedCount }}</span>
    </div>

    <div ref="fileList" class="zd-files" role="list" aria-label="下载文件状态" tabindex="0">
      <div
        v-for="(file, index) in files"
        :key="`${index}-${file.name}`"
        :ref="`fileRow${index}`"
        class="zd-file"
        :class="`is-${file.status}`"
        role="listitem"
      >
        <span class="zd-file-index">{{ index + 1 }}</span>
        <i :class="statusMeta(file.status).icon" class="zd-file-icon" />
        <div class="zd-file-main">
          <span class="zd-file-name" :title="file.name">{{ file.name }}</span>
          <span v-if="file.error" class="zd-file-error" :title="file.error">{{ file.error }}</span>
        </div>
        <span v-if="file.size" class="zd-file-size">{{ formatSize(file.size) }}</span>
        <span class="zd-file-status" :class="`is-${file.status}`">{{ statusMeta(file.status).label }}</span>
      </div>
      <div v-if="files.length === 0" class="zd-empty">正在获取文件清单...</div>
    </div>

    <div slot="footer">
      <el-button size="mini" type="danger" plain :disabled="cancelled" @click="handleCancel">
        {{ cancelled ? '正在取消...' : '取消下载' }}
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import { streamZipToDisk } from '../utils/download';

/**
 * 下载进度弹窗：清单 → 流式 zip 写盘（主路径）或逐个触发浏览器下载（降级路径）。
 * 进度、取消、结果提示都收在这里，调用方只管准备清单与保存文件名。
 * 用方：模板放 <zip-download-dialog ref="zipDl" />，下载处 this.$refs.zipDl.run(...)。
 */
export default {
  name: 'ZipDownloadDialog',

  props: {
    /** 弹窗标题：各页面口径不同（文件夹下载 / 打包下载） */
    title: { type: String, default: '打包下载' },
  },

  data() {
    return {
      visible: false,
      percent: 0,
      text: '',
      cancelled: false,
      downloadController: null,
      files: [],
    };
  },

  computed: {
    doneCount() {
      return this.files.filter(file => file.status === 'done').length;
    },
    downloadingCount() {
      return this.files.filter(file => file.status === 'downloading').length;
    },
    pendingCount() {
      return this.files.filter(file => file.status === 'pending').length;
    },
    failedCount() {
      return this.files.filter(file => file.status === 'failed').length;
    },
  },

  methods: {
    /** 文件清单一次铺满弹窗，之后只按索引更新状态，避免大列表反复重建 */
    prepareFiles(files) {
      this.files = (files || []).map((file, index) => ({
        name: file.name || `文件 ${index + 1}`,
        size: Number(file.size) || 0,
        status: 'pending',
        error: '',
      }));
      this.percent = 0;
      this.text = `待下载 ${this.files.length} 个文件`;
      this.visible = true;
      this.$nextTick(this.scrollActiveIntoView);
    },

    setFileStatus(index, status, error = '') {
      const file = this.files[index];
      if (!file) return;
      this.$set(file, 'status', status);
      this.$set(file, 'error', error || '');
      if (status === 'downloading') this.$nextTick(this.scrollActiveIntoView);
    },

    /** 文件多时在列表容器内跟随当前下载项，不牵动页面滚动 */
    scrollActiveIntoView() {
      const index = this.files.findIndex(file => file.status === 'downloading');
      if (index < 0) return;
      const list = this.$refs.fileList;
      const rowRef = this.$refs[`fileRow${index}`];
      const row = Array.isArray(rowRef) ? rowRef[0] : rowRef;
      if (!list || !row) return;
      const listRect = list.getBoundingClientRect();
      const rowRect = row.getBoundingClientRect();
      if (rowRect.top < listRect.top) list.scrollTop -= listRect.top - rowRect.top;
      else if (rowRect.bottom > listRect.bottom) list.scrollTop += rowRect.bottom - listRect.bottom;
    },

    statusMeta(status) {
      return {
        pending: { label: '待下载', icon: 'el-icon-time' },
        downloading: { label: '下载中', icon: 'el-icon-loading' },
        done: { label: '已完成', icon: 'el-icon-circle-check' },
        failed: { label: '失败', icon: 'el-icon-circle-close' },
        cancelled: { label: '已取消', icon: 'el-icon-remove-outline' },
      }[status] || { label: '待下载', icon: 'el-icon-time' };
    },

    formatSize(bytes) {
      if (!bytes) return '';
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
      return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
    },

    /** 取消下载：同时中止活动请求与写盘，无需等下一块数据到达 */
    handleCancel() {
      if (this.cancelled) return;
      this.cancelled = true;
      this.text = '正在取消...';
      if (this.downloadController) this.downloadController.abort();
    },

    /**
     * 主路径：流式拼 zip 写盘（保存框 → 拉清单 → 压缩写盘，内存恒定）。
     * @param {Function} getFiles 返回 [{name, url}] 的清单；在保存框之后调用（保住用户手势）
     * @param {String} suggestedName 保存框默认文件名
     */
    async run(getFiles, suggestedName) {
      this.cancelled = false;
      this.files = [];
      const controller = new AbortController();
      this.downloadController = controller;
      let res;
      try {
        res = await streamZipToDisk(getFiles, suggestedName, {
          signal: controller.signal,
          onStart: total => {
            this.percent = 0;
            this.text = `正在准备 ${total} 个文件...`;
          },
          onFiles: files => this.prepareFiles(files),
          onFileStatus: (index, status, error) => this.setFileStatus(index, status, error),
          onProgress: (percent, text) => {
            this.percent = percent;
            this.text = text;
          },
          onClose: () => { this.visible = false; },
          isCancelled: () => this.cancelled,
        });
      } finally {
        if (this.downloadController === controller) this.downloadController = null;
      }
      if (res.status === 'done') {
        this.$alert(
          `下载完成：${res.count} 个文件已保存为 ${suggestedName}，请到保存位置查收`,
          '下载完成',
          { confirmButtonText: '好的', type: 'success' },
        );
      } else if (res.status === 'cancelled') {
        // 保存框阶段取消是用户主动放弃，静默收场
        if (res.stage === 'download') this.$message.info('已取消下载');
      } else if (res.status === 'empty') {
        this.$message.warning('没有可打包的文件');
      } else if (res.status === 'error') {
        if (res.stage === 'save') this.$message.error(res.message);
        else if (res.stage === 'download') this.$message.error(`打包失败：${res.message}`);
        // 清单阶段的错误已由请求拦截器提示
      }
      return res;
    },

    /**
     * 降级路径（浏览器没有 showSaveFilePicker）：逐个触发下载，不拼 zip。
     * @param {Array} files [{name, url}]；文件名由预签名 URL 的 response-content-disposition 决定
     * @returns {Promise<{status:'done'|'cancelled', count}>}
     */
    async runDirect(files) {
      this.cancelled = false;
      this.percent = 0;
      this.prepareFiles(files);
      try {
        for (let i = 0; i < files.length; i++) {
          if (this.cancelled) break;
          const f = files[i];
          this.setFileStatus(i, 'downloading');
          this.text = `正在触发下载 ${i + 1}/${files.length}...`;
          // 跨源下 <a download> 被忽略，改由 URL 上的 disposition 指定名字
          const a = document.createElement('a');
          a.href = f.url;
          a.download = f.name.split('/').pop();
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          this.setFileStatus(i, 'done');
          this.percent = Math.min(99, Math.round(((i + 1) / files.length) * 100));
          this.text = `已触发 ${i + 1}/${files.length} 个文件`;
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      } catch (err) {
        const index = this.files.findIndex(file => file.status === 'downloading');
        this.setFileStatus(index, 'failed', err.message || '触发下载失败');
        throw err;
      } finally {
        this.visible = false;
      }
      // 取消只是停止后续触发，已触发的文件浏览器下载栏继续
      if (!this.cancelled) {
        this.$message({
          message: `已开始下载 ${files.length} 个文件，请留意浏览器下载项`,
          type: 'success',
          duration: 5000,
        });
      }
      return { status: this.cancelled ? 'cancelled' : 'done', count: files.length };
    },
  },
};
</script>

<style scoped>
.zd-progress-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.zd-progress-row .el-progress {
  flex: 1;
}
.zd-percent {
  width: 38px;
  color: #606266;
  font-variant-numeric: tabular-nums;
  text-align: right;
}
.zd-text {
  margin-top: 8px;
  color: #606266;
  font-size: 13px;
}
.zd-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 14px;
  margin-top: 14px;
  color: #909399;
  font-size: 12px;
}
.zd-summary-done,
.zd-file-status.is-done {
  color: #67c23a;
}
.zd-summary-active,
.zd-file-status.is-downloading {
  color: #409eff;
}
.zd-summary-failed,
.zd-file-status.is-failed {
  color: #f56c6c;
}
.zd-files {
  max-height: min(52vh, 430px);
  margin-top: 8px;
  overflow-y: auto;
  overscroll-behavior: contain;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  background: #fafbfc;
  scrollbar-gutter: stable;
}
.zd-files:focus {
  outline: 2px solid rgba(64, 158, 255, 0.28);
  outline-offset: 1px;
}
.zd-file {
  display: grid;
  grid-template-columns: 34px 18px minmax(0, 1fr) auto 62px;
  align-items: center;
  gap: 8px;
  min-height: 42px;
  padding: 7px 12px;
  border-bottom: 1px solid #ebeef5;
  color: #303133;
  transition: background-color 0.15s ease;
}
.zd-file:last-child {
  border-bottom: 0;
}
.zd-file.is-downloading {
  background: #ecf5ff;
}
.zd-file.is-done {
  background: #f0f9eb;
}
.zd-file.is-failed {
  background: #fef0f0;
}
.zd-file-index {
  color: #c0c4cc;
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  text-align: right;
}
.zd-file-icon {
  color: #c0c4cc;
}
.zd-file.is-downloading .zd-file-icon {
  color: #409eff;
}
.zd-file.is-done .zd-file-icon {
  color: #67c23a;
}
.zd-file.is-failed .zd-file-icon {
  color: #f56c6c;
}
.zd-file-main {
  display: flex;
  min-width: 0;
  flex-direction: column;
}
.zd-file-name {
  overflow: hidden;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.zd-file-error {
  overflow: hidden;
  margin-top: 2px;
  color: #f56c6c;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.zd-file-size,
.zd-file-status {
  color: #909399;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.zd-file-status {
  text-align: right;
}
.zd-empty {
  padding: 32px 16px;
  color: #909399;
  font-size: 13px;
  text-align: center;
}
</style>

<style>
.download-progress-dialog {
  display: flex;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 40px);
  flex-direction: column;
  margin-top: 5vh !important;
}
.download-progress-dialog .el-dialog__body {
  min-height: 0;
  overflow: hidden;
}
</style>
